import {
  BarChart3,
  Bot,
  BookOpen,
  CheckCircle2,
  Clipboard,
  Database,
  Gauge,
  Gem,
  LayoutDashboard,
  Layers,
  Lightbulb,
  LogOut,
  MessageSquare,
  Pencil,
  Plus,
  Rocket,
  RefreshCw,
  Send,
  Settings,
  Shield,
  Sparkles,
  Upload,
  Trash2,
  UserPlus,
  Users,
  XCircle
} from "lucide-react";
import { createContext, FormEvent, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { api, ApiError, streamApi } from "./lib/api";

type User = { id: string; email: string; role: "user" | "admin"; status: string };
type Agent = {
  id: string;
  name: string;
  slug: string;
  guide_text: string;
  system_prompt?: string;
  model_config_id?: string | null;
  model_name?: string;
  status: "active" | "draft" | "disabled";
  sort_order: number;
  temperature?: number;
  max_output_tokens?: number;
  history_message_limit?: number;
};
type Conversation = { id: string; title: string; agent_id: string; updated_at: string };
type ChatMessage = { id: string; role: "user" | "assistant" | "system"; content: string; created_at?: string };
type ModelConfig = {
  id: string;
  name: string;
  provider: string;
  model: string;
  base_url: string;
  endpoint: string;
  api_key_secret_name: string;
  is_default: number;
  enabled: number;
  extra_json: string | null;
};
type UsageEvent = {
  id: string;
  status: "success" | "error";
  user_email?: string;
  agent_name?: string;
  model_name?: string;
  tokens_in: number;
  tokens_out: number;
  latency_ms: number;
  error_message?: string;
  created_at: string;
};

const outputLengthOptions = [
  { key: "brief", label: "精简", words: 300 },
  { key: "standard", label: "标准", words: 800 },
  { key: "detailed", label: "详细", words: 1500 },
  { key: "custom", label: "自定义", words: 1200 }
] as const;

const knowledgeInterview = [
  {
    label: "成就故事",
    question: "讲一个你最有成就感的瞬间：你帮谁解决了什么，当时发生了什么？",
    placeholder: "例如：一个学员读了很多书却用不上，我带他把知识拆成可复用卡片..."
  },
  {
    label: "反共识",
    question: "在那个故事里，你做的事和大多数人最不一样的地方是什么？",
    placeholder: "例如：别人追求读更多，我让人读更少但用更狠。"
  },
  {
    label: "步骤框架",
    question: "如果把你帮人成长的过程拆成几个阶段，大概是哪几步？",
    placeholder: "例如：筛信息、搭框架、讲出来、反复复用。"
  },
  {
    label: "口头禅",
    question: "你最常对学员说、几乎成了口头禅的一句话是什么？",
    placeholder: "例如：懂不等于会，讲得出来才算数。"
  },
  {
    label: "专属概念",
    question: "有没有哪个概念，是你自己琢磨出来、外面很少有人这么说的？",
    placeholder: "例如：最小可行知识、知识利息、复利式输出..."
  },
  {
    label: "北极星",
    question: "你希望学完你方法的人，三个月后变成什么样的人？",
    placeholder: "例如：从学了很多但焦虑，变成学得不多但每一点都能产出。"
  }
] as const;

const knowledgeAssetTypes = ["文章/公众号", "视频/课程", "音频/播客", "直播回放", "PDF/讲义", "聊天/问答记录"] as const;

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthContext is missing");
  return value;
}

function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const data = await api<{ user: User | null }>("/api/auth/me");
    setUser(data.user);
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      refresh,
      login: async (email, password) => {
        const data = await api<{ user: User }>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password })
        });
        setUser(data.user);
        return data.user;
      },
      logout: async () => {
        await api("/api/auth/logout", { method: "POST" });
        setUser(null);
      }
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function LoadingScreen() {
  return (
    <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-500">
      <RefreshCw className="mb-3 h-6 w-6 animate-spin text-brand" />
      加载中
    </div>
  );
}

function ProtectedRoute({ children, loginPath = "/user/login" }: { children: ReactNode; loginPath?: string }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to={loginPath} replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/user/workbench" replace />;
  return <>{children}</>;
}

function Shell({ children, area = "user" }: { children: ReactNode; area?: "user" | "admin" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links =
    area === "admin"
      ? [
          { to: "/admin", label: "管理", icon: Shield },
          { to: "/admin/agents", label: "智能体", icon: Bot },
          { to: "/admin/model-configs", label: "模型", icon: Settings },
          { to: "/admin/users", label: "用户", icon: Users },
          { to: "/admin/usage", label: "记录", icon: BarChart3 }
        ]
      : [{ to: "/user/workbench", label: "工作台", icon: LayoutDashboard }];
  const homePath = area === "admin" ? "/admin" : "/user/workbench";
  const logoutPath = area === "admin" ? "/admin/login" : "/user/login";

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-line bg-white lg:block">
        <div className="flex h-16 items-center gap-3 border-b border-line px-5">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-brand text-white">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-ink">知识掘金藏宝阁</div>
            <div className="text-xs text-slate-500">{area === "admin" ? "Admin Console" : "User Workbench"}</div>
          </div>
        </div>
        <nav className="space-y-1 px-3 py-4">
          {links.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/admin" || item.to === "/user/workbench"}
                className={({ isActive }) =>
                  `flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium ${
                    isActive ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-100 hover:text-ink"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
        </nav>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-line bg-white/90 px-4 backdrop-blur lg:px-8">
          <Link to={homePath} className="flex items-center gap-2 text-sm font-semibold text-ink lg:hidden">
            <Sparkles className="h-5 w-5 text-brand" />
            知识掘金藏宝阁
          </Link>
          <div className="hidden text-sm text-slate-500 lg:block">
            {area === "admin" ? "管理员后台 · 智能体与模型配置 · DeepSeek V4 Pro" : "用户工作台 · 知识内容智能体对话 · DeepSeek V4 Pro"}
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-medium text-ink">{user?.email}</div>
              <div className="text-xs text-slate-500">{user?.role === "admin" ? "管理员" : "普通用户"}</div>
            </div>
            <button className="btn btn-secondary" onClick={() => logout().then(() => navigate(logoutPath))} title="退出登录">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">退出</span>
            </button>
          </div>
        </header>
        <main className="px-4 py-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}

function EntrancePage() {
  const { loading } = useAuth();
  if (loading) return <LoadingScreen />;

  return (
    <div className="grid min-h-screen place-items-center bg-[#eef3f8] px-4">
      <div className="w-full max-w-3xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-md bg-brand text-white">
            <Sparkles className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-semibold text-ink">知识掘金藏宝阁</h1>
          <p className="mt-2 text-sm text-slate-500">请选择入口</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Link to="/user/login" className="panel rounded-lg p-6 transition hover:-translate-y-0.5 hover:border-brand/40">
            <LayoutDashboard className="mb-5 h-8 w-8 text-brand" />
            <h2 className="text-xl font-semibold text-ink">用户入口</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">进入工作台，选择智能体并开始文本对话。</p>
          </Link>
          <Link to="/admin/login" className="panel rounded-lg p-6 transition hover:-translate-y-0.5 hover:border-brand/40">
            <Shield className="mb-5 h-8 w-8 text-cyan" />
            <h2 className="text-xl font-semibold text-ink">管理员入口</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">管理智能体、模型配置、用户和使用记录。</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

function LoginPage({ mode }: { mode: "user" | "admin" }) {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(mode === "admin" ? "admin@example.com" : "user@example.com");
  const [password, setPassword] = useState(mode === "admin" ? "Admin@123456" : "User@123456");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) {
    if (mode === "admin" && user.role !== "admin") {
      // Keep the admin login form accessible so a normal-user session can switch accounts.
    } else {
      return <Navigate to={mode === "admin" ? "/admin" : "/user/workbench"} replace />;
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const loggedIn = await login(email, password);
      if (mode === "admin" && loggedIn.role !== "admin") {
        await logout();
        setError("该账号不是管理员，请从用户入口登录");
        return;
      }
      navigate(mode === "admin" ? "/admin" : "/user/workbench");
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#eef3f8] px-4">
      <div className="panel w-full max-w-md rounded-lg p-8">
        <div className="mb-8 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-md bg-brand text-white">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-ink">{mode === "admin" ? "管理员入口" : "用户入口"}</h1>
            <p className="text-sm text-slate-500">{mode === "admin" ? "登录后进入管理后台" : "登录后进入智能体工作台"}</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label">邮箱</label>
            <input className="input" value={email} onChange={(event) => setEmail(event.target.value)} />
          </div>
          <div>
            <label className="label">密码</label>
            <input className="input" type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </div>
          {error ? <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
          <button className="btn btn-primary w-full" disabled={busy}>
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : mode === "admin" ? <Shield className="h-4 w-4" /> : <LayoutDashboard className="h-4 w-4" />}
            登录
          </button>
        </form>
        <div className="mt-4 text-center text-sm">
          <Link className="text-brand hover:text-blue-700" to={mode === "admin" ? "/user/login" : "/admin/login"}>
            切换到{mode === "admin" ? "用户入口" : "管理员入口"}
          </Link>
        </div>
        <div className="mt-5 rounded-md border border-line bg-slate-50 p-3 text-xs leading-6 text-slate-500">
          管理员：admin@example.com / Admin@123456
          <br />
          普通用户：user@example.com / User@123456
        </div>
      </div>
    </div>
  );
}

function LegacyAgentRedirect() {
  const { slug = "" } = useParams();
  return <Navigate to={`/user/agents/${slug}`} replace />;
}

function WorkbenchPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api<{ agents: Agent[] }>("/api/agents")
      .then((data) => setAgents(data.agents))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Shell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink">工作台</h1>
          <p className="mt-1 text-sm text-slate-500">选择一个场景智能体，进入统一聊天页面。</p>
        </div>
        <div className="rounded-md border border-line bg-white px-3 py-2 text-sm text-slate-600">
          <Bot className="mr-2 inline h-4 w-4 text-cyan" />
          {agents.length} 个可用智能体
        </div>
      </div>
      {loading ? (
        <LoadingBlock />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {agents.map((agent, index) => (
            <Link
              key={agent.id}
              to={`/user/agents/${agent.slug}`}
              className={`panel rounded-lg p-5 transition hover:-translate-y-0.5 hover:border-brand/40 ${agent.slug === "knowledge-digger" ? "knowledge-agent-card" : ""}`}
            >
              <div className="mb-5 flex items-start justify-between gap-3">
                <div className={`grid h-11 w-11 place-items-center rounded-md ${agent.slug === "knowledge-digger" ? "bg-brand text-white" : "bg-slate-100 text-brand"}`}>
                  {agent.slug === "knowledge-digger" ? <Gem className="h-5 w-5" /> : index % 2 === 0 ? <MessageSquare className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${agent.slug === "knowledge-digger" ? "bg-amber-50 text-amber" : "bg-emerald-50 text-emerald-700"}`}>
                  {agent.slug === "knowledge-digger" ? "featured" : "active"}
                </span>
              </div>
              <h2 className="text-lg font-semibold text-ink">{agent.name}</h2>
              <p className="mt-2 min-h-16 text-sm leading-6 text-slate-500">{agent.guide_text}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-brand">
                开始对话
                <Send className="h-4 w-4" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </Shell>
  );
}

function LoadingBlock() {
  return (
    <div className="panel grid h-48 place-items-center rounded-lg text-sm text-slate-500">
      <RefreshCw className="mb-2 h-5 w-5 animate-spin text-brand" />
      正在加载
    </div>
  );
}

function KnowledgeDiggerStarter({ agent, onSubmit, streaming }: { agent: Agent | null; onSubmit: (content: string) => void; streaming: boolean }) {
  const [profile, setProfile] = useState({ name: "", field: "", audience: "" });
  const [answers, setAnswers] = useState(() => knowledgeInterview.map(() => ""));
  const [assets, setAssets] = useState([
    { type: "文章/公众号", title: "", detail: "" },
    { type: "视频/课程", title: "", detail: "" }
  ]);
  const [goal, setGoal] = useState("生成专属方法论框架、概念图谱、白皮书大纲和变现产品方案");

  const answeredCount = answers.filter((item) => item.trim()).length;
  const assetCount = assets.filter((item) => item.title.trim() || item.detail.trim()).length;
  const canSubmit = answeredCount >= 3 || profile.field.trim() || assetCount > 0;

  const updateAnswer = (index: number, value: string) => {
    setAnswers((prev) => prev.map((item, idx) => (idx === index ? value : item)));
  };

  const updateAsset = (index: number, key: "type" | "title" | "detail", value: string) => {
    setAssets((prev) => prev.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)));
  };

  const addAsset = () => setAssets((prev) => [...prev, { type: "PDF/讲义", title: "", detail: "" }]);

  const submit = () => {
    const prompt = [
      "请基于以下访谈与素材，为我萃取一套可命名、可传播、可交付、可变现的专属方法论。",
      "",
      "【创作者画像】",
      `姓名/昵称：${profile.name || "未填写"}`,
      `领域：${profile.field || "未填写"}`,
      `目标用户：${profile.audience || "未填写"}`,
      "",
      "【AI 访谈回答】",
      ...knowledgeInterview.map((item, index) => `${index + 1}. ${item.question}\n回答：${answers[index] || "未填写"}`),
      "",
      "【数字资产】",
      ...assets
        .filter((item) => item.title.trim() || item.detail.trim())
        .map((item, index) => `${index + 1}. 类型：${item.type}；标题：${item.title || "未命名"}；说明/摘录：${item.detail || "未填写"}`),
      assets.some((item) => item.title.trim() || item.detail.trim()) ? "" : "暂无素材，请主要依据访谈回答推导。",
      "",
      "【本次目标】",
      goal,
      "",
      "请严格输出以下结构：",
      "1. 方法论命名：给出 3 个候选名，并选择最佳命名，解释命名理由。",
      "2. 一句话定位：用一句话说清这套方法解决什么问题、适合谁、凭什么有效。",
      "3. 北极星：学完这套方法的人会发生什么变化。",
      "4. 核心框架：拆成 3-5 个模块，每个模块包含名称、定义、关键动作、常见误区、可验证成果。",
      "5. 专属概念词典：提炼 6-10 个专属概念，每个概念给定义、来源线索、应用场景。",
      "6. 概念关系图谱：用文字描述节点与连接关系，说明哪些概念互相驱动。",
      "7. 白皮书大纲：给出章节标题、每章核心观点、可放入的故事或案例。",
      "8. 变现方案：设计电子书、训练营、咨询/陪跑 3 类产品，包含价格建议、交付物、首批上线步骤。",
      "9. 下一步采集问题：列出还需要向我追问的 5 个高价值问题，让方法论更准确。"
    ].join("\n");
    onSubmit(prompt);
  };

  return (
    <div className="knowledge-canvas">
      <section className="knowledge-hero">
        <div className="knowledge-hero-copy">
          <div className="knowledge-mark">
            <Gem className="h-5 w-5" />
            给知识博主的方法论萃取器
          </div>
          <h2>{agent?.name ?? "知识掘金"}</h2>
          <p>{agent?.guide_text}</p>
          <div className="knowledge-stats">
            <span><strong>{answeredCount}/6</strong>访谈回答</span>
            <span><strong>{assetCount}</strong>份素材</span>
            <span><strong>DeepSeek</strong>V4 Pro</span>
          </div>
        </div>
        <div className="knowledge-flow">
          {[
            { icon: MessageSquare, title: "AI 访谈", sub: "讲故事，挖反共识" },
            { icon: Upload, title: "数字资产", sub: "文章、课程、讲义" },
            { icon: Sparkles, title: "萃取处理", sub: "观点聚类与命名" },
            { icon: Rocket, title: "变现发布", sub: "产品与交付方案" }
          ].map((item, index) => (
            <div key={item.title} className="knowledge-flow-item">
              <div className="knowledge-flow-icon"><item.icon className="h-5 w-5" /></div>
              <div>
                <div className="text-sm font-semibold text-ink">{item.title}</div>
                <div className="text-xs text-slate-500">{item.sub}</div>
              </div>
              <span>{String(index + 1).padStart(2, "0")}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="knowledge-section">
        <div className="knowledge-section-title">
          <Lightbulb className="h-5 w-5 text-brand" />
          <div>
            <h3>创作者画像</h3>
            <p>先让模型知道你是谁、你服务谁。</p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <input className="input" placeholder="姓名/昵称" value={profile.name} onChange={(event) => setProfile({ ...profile, name: event.target.value })} />
          <input className="input" placeholder="领域，例如：高效学习/知识管理" value={profile.field} onChange={(event) => setProfile({ ...profile, field: event.target.value })} />
          <input className="input" placeholder="目标用户，例如：职场新人/创业者" value={profile.audience} onChange={(event) => setProfile({ ...profile, audience: event.target.value })} />
        </div>
      </section>

      <section className="knowledge-section">
        <div className="knowledge-section-title">
          <MessageSquare className="h-5 w-5 text-brand" />
          <div>
            <h3>AI 访谈</h3>
            <p>按原型的 6 个问题，把经验背后的逻辑讲出来。</p>
          </div>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          {knowledgeInterview.map((item, index) => (
            <label key={item.label} className="knowledge-question">
              <span>{index + 1}. {item.label}</span>
              <strong>{item.question}</strong>
              <textarea value={answers[index]} placeholder={item.placeholder} onChange={(event) => updateAnswer(index, event.target.value)} />
            </label>
          ))}
        </div>
      </section>

      <section className="knowledge-section">
        <div className="knowledge-section-title">
          <Layers className="h-5 w-5 text-brand" />
          <div>
            <h3>数字资产</h3>
            <p>把已有文章、课程、直播、讲义作为原矿，模型会一起萃取。</p>
          </div>
          <button className="btn btn-secondary ml-auto" type="button" onClick={addAsset}>
            <Plus className="h-4 w-4" />
            添加素材
          </button>
        </div>
        <div className="space-y-3">
          {assets.map((asset, index) => (
            <div key={index} className="grid gap-3 rounded-md border border-line bg-slate-50 p-3 md:grid-cols-[160px_minmax(0,1fr)_minmax(0,1.4fr)]">
              <select className="input" value={asset.type} onChange={(event) => updateAsset(index, "type", event.target.value)}>
                {knowledgeAssetTypes.map((type) => <option key={type}>{type}</option>)}
              </select>
              <input className="input" placeholder="素材标题" value={asset.title} onChange={(event) => updateAsset(index, "title", event.target.value)} />
              <input className="input" placeholder="摘要、链接或关键摘录" value={asset.detail} onChange={(event) => updateAsset(index, "detail", event.target.value)} />
            </div>
          ))}
        </div>
      </section>

      <section className="knowledge-section knowledge-output">
        <div className="knowledge-preview-card">
          <BookOpen className="h-5 w-5 text-amber" />
          <div>
            <h3>预期成果</h3>
            <p>方法论命名、核心框架、概念词典、图谱关系、白皮书大纲、电子书/训练营/咨询变现方案。</p>
          </div>
        </div>
        <div className="knowledge-goal">
          <label className="label">本次萃取目标</label>
          <textarea className="input" rows={3} value={goal} onChange={(event) => setGoal(event.target.value)} />
          <button className="btn btn-primary mt-4 w-full" type="button" disabled={streaming || !canSubmit} onClick={submit}>
            {streaming ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            开始萃取我的方法论
          </button>
          {!canSubmit ? <p className="mt-2 text-xs text-slate-400">至少填写领域、素材，或 3 个访谈回答。</p> : null}
        </div>
      </section>
    </div>
  );
}

function ChatPage() {
  const { slug = "" } = useParams();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentId, setCurrentId] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [outputLength, setOutputLength] = useState<(typeof outputLengthOptions)[number]["key"]>("standard");
  const [customWords, setCustomWords] = useState(1200);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const targetWords = outputLength === "custom" ? customWords : outputLengthOptions.find((item) => item.key === outputLength)?.words ?? 800;

  const loadConversations = async () => {
    const data = await api<{ conversations: Conversation[] }>(`/api/conversations?slug=${encodeURIComponent(slug)}`);
    setConversations(data.conversations);
    if (!currentId && data.conversations[0]) setCurrentId(data.conversations[0].id);
    return data.conversations;
  };

  const loadMessages = async (conversationId: string) => {
    const data = await api<{ messages: ChatMessage[] }>(`/api/conversations/${conversationId}/messages`);
    setMessages(data.messages);
  };

  useEffect(() => {
    setCurrentId("");
    setMessages([]);
    Promise.all([api<{ agent: Agent }>(`/api/agents/${slug}`).then((data) => setAgent(data.agent)), loadConversations()]).catch((err) =>
      setError(err instanceof Error ? err.message : "页面加载失败")
    );
  }, [slug]);

  useEffect(() => {
    if (currentId) loadMessages(currentId).catch((err) => setError(err instanceof Error ? err.message : "消息加载失败"));
  }, [currentId]);

  const createConversation = async () => {
    const data = await api<{ conversation: Conversation }>("/api/conversations", {
      method: "POST",
      body: JSON.stringify({ slug })
    });
    setCurrentId(data.conversation.id);
    await loadConversations();
    return data.conversation.id;
  };

  const sendMessage = async (content = input) => {
    const text = content.trim();
    if (!text || streaming) return;
    setInput("");
    setError("");
    setStreaming(true);
    let conversationId = currentId;
    try {
      if (!conversationId) conversationId = await createConversation();
      const userMessage: ChatMessage = { id: `local-user-${Date.now()}`, role: "user", content: text };
      const assistantMessage: ChatMessage = { id: `local-assistant-${Date.now()}`, role: "assistant", content: "" };
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      await streamApi(
        `/api/conversations/${conversationId}/messages/stream`,
        { content: text, target_words: targetWords },
        {
          onDelta: (delta) =>
            setMessages((prev) => prev.map((message) => (message.id === assistantMessage.id ? { ...message, content: message.content + delta } : message))),
          onDone: () => undefined,
          onError: (message) => setError(message)
        }
      );
      await Promise.all([loadMessages(conversationId), loadConversations()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "发送失败");
    } finally {
      setStreaming(false);
    }
  };

  const regenerate = () => {
    const lastUser = [...messages].reverse().find((message) => message.role === "user");
    if (lastUser) void sendMessage(lastUser.content);
  };

  return (
    <Shell>
      <div className="grid min-h-[calc(100vh-7rem)] gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
        <section className="panel rounded-lg">
          <div className="flex items-center justify-between border-b border-line p-4">
            <div>
              <div className="text-sm font-semibold text-ink">历史会话</div>
              <div className="text-xs text-slate-500">{agent?.name ?? "智能体"}</div>
            </div>
            <button className="btn btn-secondary h-9 px-2" onClick={() => createConversation()} title="新建会话">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[calc(100vh-12rem)] overflow-auto p-2">
            {conversations.length === 0 ? <div className="p-4 text-sm text-slate-500">还没有会话</div> : null}
            {conversations.map((item) => (
              <button
                key={item.id}
                className={`mb-1 w-full rounded-md px-3 py-2 text-left text-sm ${
                  item.id === currentId ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"
                }`}
                onClick={() => setCurrentId(item.id)}
              >
                <div className="truncate font-medium">{item.title}</div>
                <div className="mt-1 text-xs text-slate-400">{new Date(item.updated_at).toLocaleString()}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="panel flex min-h-[calc(100vh-7rem)] flex-col rounded-lg">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line p-4">
            <div>
              <h1 className="text-lg font-semibold text-ink">{agent?.name ?? "聊天"}</h1>
              <p className="mt-1 text-sm text-slate-500">{agent?.guide_text}</p>
            </div>
            <button className="btn btn-secondary" disabled={!messages.some((message) => message.role === "user") || streaming} onClick={regenerate}>
              <RefreshCw className="h-4 w-4" />
              重新生成
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-slate-50/70 p-4">
            {messages.length === 0 && slug === "knowledge-digger" ? (
              <KnowledgeDiggerStarter agent={agent} streaming={streaming} onSubmit={(content) => void sendMessage(content)} />
            ) : messages.length === 0 ? (
              <div className="mx-auto mt-10 max-w-2xl rounded-lg border border-dashed border-line bg-white p-8 text-center">
                <Sparkles className="mx-auto mb-4 h-8 w-8 text-brand" />
                <h2 className="text-lg font-semibold text-ink">{agent?.name}</h2>
                <p className="mt-3 text-sm leading-6 text-slate-500">{agent?.guide_text}</p>
              </div>
            ) : (
              <div className="mx-auto flex max-w-4xl flex-col gap-4">
                {messages.map((message) => (
                  <div key={message.id} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[86%] rounded-lg px-4 py-3 text-sm leading-6 ${
                        message.role === "user" ? "bg-brand text-white" : "border border-line bg-white text-slate-700"
                      }`}
                    >
                      <pre className="whitespace-pre-wrap break-words font-sans">{message.content || (streaming ? "正在生成..." : "")}</pre>
                      {message.role === "assistant" && message.content ? (
                        <button className="mt-2 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-brand" onClick={() => navigator.clipboard.writeText(message.content)}>
                          <Clipboard className="h-3.5 w-3.5" />
                          复制
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error ? <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div> : null}
          <form
            className="border-t border-line bg-white p-4"
            onSubmit={(event) => {
              event.preventDefault();
              void sendMessage();
            }}
          >
            <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium text-slate-600">生成长度</span>
              <div className="inline-flex rounded-md border border-line bg-slate-50 p-1">
                {outputLengthOptions.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`h-8 rounded px-3 text-xs font-medium transition ${
                      outputLength === item.key ? "bg-white text-brand shadow-sm" : "text-slate-500 hover:text-ink"
                    }`}
                    onClick={() => setOutputLength(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {outputLength === "custom" ? (
                <label className="flex h-9 items-center gap-2 rounded-md border border-line bg-white px-2 text-xs text-slate-500">
                  约
                  <input
                    className="w-20 border-0 bg-transparent text-sm font-medium text-ink outline-none"
                    type="number"
                    min={100}
                    max={10000}
                    step={100}
                    value={customWords}
                    onChange={(event) => setCustomWords(Math.max(100, Math.min(10000, Number(event.target.value) || 100)))}
                  />
                  字
                </label>
              ) : (
                <span className="text-xs text-slate-400">约 {targetWords} 字</span>
              )}
            </div>
            <div className="flex gap-3">
              <textarea
                className="input min-h-12 flex-1"
                placeholder="输入你的需求，Shift + Enter 换行"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void sendMessage();
                  }
                }}
              />
              <button className="btn btn-primary h-auto w-12 px-0" disabled={streaming || !input.trim()} title="发送">
                {streaming ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </button>
            </div>
          </form>
        </section>
      </div>
    </Shell>
  );
}

function AdminDashboard() {
  const [overview, setOverview] = useState({ users: 0, agents: 0, conversations: 0, usageToday: 0 });
  useEffect(() => {
    api<{ overview: typeof overview }>("/api/admin/overview").then((data) => setOverview(data.overview));
  }, []);
  const stats = [
    { label: "用户", value: overview.users, icon: Users },
    { label: "智能体", value: overview.agents, icon: Bot },
    { label: "会话", value: overview.conversations, icon: MessageSquare },
    { label: "今日调用", value: overview.usageToday, icon: Gauge }
  ];
  return (
    <Shell area="admin">
      <h1 className="mb-6 text-2xl font-semibold text-ink">管理后台</h1>
      <div className="grid gap-4 md:grid-cols-4">
        {stats.map((item) => (
          <div key={item.label} className="panel rounded-lg p-5">
            <item.icon className="mb-4 h-5 w-5 text-brand" />
            <div className="text-3xl font-semibold text-ink">{item.value}</div>
            <div className="mt-1 text-sm text-slate-500">{item.label}</div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

const blankAgent = {
  name: "",
  guide_text: "",
  system_prompt: "",
  model_config_id: "",
  status: "draft",
  sort_order: 0,
  temperature: 0.7,
  max_output_tokens: 1800,
  history_message_limit: 12
};

function AdminAgents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [form, setForm] = useState({ ...blankAgent });
  const [editingId, setEditingId] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    const [agentData, modelData] = await Promise.all([
      api<{ agents: Agent[] }>("/api/admin/agents"),
      api<{ modelConfigs: ModelConfig[] }>("/api/admin/model-configs")
    ]);
    setAgents(agentData.agents);
    setModels(modelData.modelConfigs);
  };
  useEffect(() => {
    void load();
  }, []);

  const edit = (agent: Agent) => {
    setEditingId(agent.id);
    setForm({
      name: agent.name,
      guide_text: agent.guide_text,
      system_prompt: agent.system_prompt ?? "",
      model_config_id: agent.model_config_id ?? "",
      status: agent.status,
      sort_order: agent.sort_order ?? 0,
      temperature: agent.temperature ?? 0.7,
      max_output_tokens: agent.max_output_tokens ?? 1800,
      history_message_limit: agent.history_message_limit ?? 12
    });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setNotice("");
    try {
      const body = {
        ...form,
        model_config_id: form.model_config_id || null,
        sort_order: Number(form.sort_order),
        temperature: Number(form.temperature),
        max_output_tokens: Number(form.max_output_tokens),
        history_message_limit: Number(form.history_message_limit)
      };
      await api(editingId ? `/api/admin/agents/${editingId}` : "/api/admin/agents", {
        method: editingId ? "PATCH" : "POST",
        body: JSON.stringify(body)
      });
      setNotice(editingId ? "已更新智能体" : "已创建智能体");
      setEditingId("");
      setForm({ ...blankAgent });
      await load();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "保存失败");
    }
  };

  const deleteAgent = async (agent: Agent) => {
    const ok = window.confirm(`确定删除「${agent.name}」吗？删除后工作台和后台列表都不会再显示。`);
    if (!ok) return;
    setNotice("");
    try {
      await api(`/api/admin/agents/${agent.id}`, { method: "DELETE" });
      if (editingId === agent.id) {
        setEditingId("");
        setForm({ ...blankAgent });
      }
      setNotice("已删除智能体");
      await load();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "删除失败");
    }
  };

  return (
    <Shell area="admin">
      <div className="mb-5 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink">智能体管理</h1>
        <button
          className="btn btn-secondary"
          onClick={() => {
            setEditingId("");
            setForm({ ...blankAgent });
          }}
        >
          <Plus className="h-4 w-4" />
          新建
        </button>
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="panel overflow-hidden rounded-lg">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr>
                <th className="table-th w-[44%]">名称</th>
                <th className="table-th w-[13%]">状态</th>
                <th className="table-th w-[19%]">模型</th>
                <th className="table-th w-[8%]">排序</th>
                <th className="table-th w-[16%]">操作</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.id}>
                  <td className="table-td">
                    <div className="font-medium text-ink">{agent.name}</div>
                    <div className="mt-1 max-w-lg truncate text-xs text-slate-500">{agent.guide_text}</div>
                  </td>
                  <td className="table-td"><StatusPill status={agent.status} /></td>
                  <td className="table-td">{agent.model_name ?? "默认模型"}</td>
                  <td className="table-td">{agent.sort_order}</td>
                  <td className="table-td">
                    <div className="flex flex-nowrap gap-2">
                      <button className="btn btn-secondary h-8 w-8 px-0" onClick={() => edit(agent)} title="编辑智能体">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button className="btn h-8 w-8 border border-red-200 bg-white px-0 text-red-600 hover:bg-red-50" onClick={() => deleteAgent(agent)} title="删除智能体">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form onSubmit={submit} className="panel rounded-lg p-5">
          <h2 className="mb-4 text-base font-semibold text-ink">{editingId ? "编辑智能体" : "新建智能体"}</h2>
          <FormInput label="名称" value={form.name} onChange={(value) => setForm({ ...form, name: value })} />
          <FormTextarea label="引导语" rows={3} value={form.guide_text} onChange={(value) => setForm({ ...form, guide_text: value })} />
          <FormTextarea label="智能体提示词" rows={8} value={form.system_prompt} onChange={(value) => setForm({ ...form, system_prompt: value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">状态</label>
              <select className="input" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as Agent["status"] })}>
                <option value="draft">draft</option>
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
            </div>
            <div>
              <label className="label">模型</label>
              <select className="input" value={form.model_config_id} onChange={(event) => setForm({ ...form, model_config_id: event.target.value })}>
                <option value="">默认模型</option>
                {models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
            <FormNumber label="排序" value={form.sort_order} onChange={(value) => setForm({ ...form, sort_order: value })} />
            <FormNumber label="温度" value={form.temperature} step={0.1} onChange={(value) => setForm({ ...form, temperature: value })} />
            <FormNumber label="最大输出" value={form.max_output_tokens} onChange={(value) => setForm({ ...form, max_output_tokens: value })} />
            <FormNumber label="上下文条数" value={form.history_message_limit} onChange={(value) => setForm({ ...form, history_message_limit: value })} />
          </div>
          {notice ? <div className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{notice}</div> : null}
          <button className="btn btn-primary mt-5 w-full">
            <CheckCircle2 className="h-4 w-4" />
            保存
          </button>
        </form>
      </div>
    </Shell>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone = status === "active" ? "bg-emerald-50 text-emerald-700" : status === "draft" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>{status}</span>;
}

function FormInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <div className="mb-3">
      <label className="label">{label}</label>
      <input className="input" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function FormTextarea({ label, rows, value, onChange }: { label: string; rows: number; value: string; onChange: (value: string) => void }) {
  return (
    <div className="mb-3">
      <label className="label">{label}</label>
      <textarea className="input" rows={rows} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function FormNumber({ label, value, step = 1, onChange }: { label: string; value: number; step?: number; onChange: (value: number) => void }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" type="number" step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </div>
  );
}

function AdminModelConfigs() {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [notice, setNotice] = useState("");
  const load = () => api<{ modelConfigs: ModelConfig[] }>("/api/admin/model-configs").then((data) => setModels(data.modelConfigs));
  useEffect(() => {
    void load();
  }, []);
  const patch = async (id: string, body: Partial<Omit<ModelConfig, "enabled" | "is_default">> & { enabled?: boolean; is_default?: boolean }) => {
    await api(`/api/admin/model-configs/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    setNotice("模型配置已更新");
    await load();
  };
  return (
    <Shell area="admin">
      <h1 className="mb-5 text-2xl font-semibold text-ink">模型配置</h1>
      <div className="panel overflow-hidden rounded-lg">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="table-th">名称</th>
              <th className="table-th">Provider</th>
              <th className="table-th">模型</th>
              <th className="table-th">密钥</th>
              <th className="table-th">状态</th>
              <th className="table-th">操作</th>
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <tr key={model.id}>
                <td className="table-td">
                  <div className="font-medium text-ink">{model.name}</div>
                  <div className="text-xs text-slate-500">{model.base_url}{model.endpoint}</div>
                </td>
                <td className="table-td">{model.provider}</td>
                <td className="table-td">{model.model}</td>
                <td className="table-td">{model.api_key_secret_name}</td>
                <td className="table-td">
                  {model.is_default ? <span className="mr-2 rounded-full bg-blue-50 px-2 py-1 text-xs text-brand">default</span> : null}
                  {model.enabled ? <StatusPill status="active" /> : <StatusPill status="disabled" />}
                </td>
                <td className="table-td">
                  <div className="flex gap-2">
                    <button className="btn btn-secondary h-8" onClick={() => patch(model.id, { is_default: true })}>设默认</button>
                    <button className="btn btn-secondary h-8" onClick={() => patch(model.id, { enabled: !model.enabled })}>
                      {model.enabled ? "停用" : "启用"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {notice ? <div className="mt-4 text-sm text-slate-500">{notice}</div> : null}
    </Shell>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [notice, setNotice] = useState("");
  const load = () => api<{ users: User[] }>("/api/admin/users").then((data) => setUsers(data.users));
  useEffect(() => {
    void load();
  }, []);
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/admin/users", {
        method: "POST",
        body: JSON.stringify(Object.fromEntries(form.entries()))
      });
      event.currentTarget.reset();
      setNotice("用户已创建");
      await load();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "创建失败");
    }
  };
  const update = async (id: string, body: Partial<User>) => {
    await api(`/api/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    await load();
  };
  return (
    <Shell area="admin">
      <h1 className="mb-5 text-2xl font-semibold text-ink">用户管理</h1>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="panel overflow-hidden rounded-lg">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="table-th">邮箱</th>
                <th className="table-th">角色</th>
                <th className="table-th">状态</th>
                <th className="table-th">操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map((item) => (
                <tr key={item.id}>
                  <td className="table-td">{item.email}</td>
                  <td className="table-td">{item.role}</td>
                  <td className="table-td"><StatusPill status={item.status} /></td>
                  <td className="table-td">
                    <div className="flex gap-2">
                      <button className="btn btn-secondary h-8" onClick={() => update(item.id, { role: item.role === "admin" ? "user" : "admin" })}>
                        切换角色
                      </button>
                      <button className="btn btn-secondary h-8" onClick={() => update(item.id, { status: item.status === "active" ? "disabled" : "active" })}>
                        {item.status === "active" ? "禁用" : "启用"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form className="panel rounded-lg p-5" onSubmit={create}>
          <h2 className="mb-4 text-base font-semibold text-ink">创建用户</h2>
          <label className="label">邮箱</label>
          <input className="input mb-3" name="email" />
          <label className="label">初始密码</label>
          <input className="input mb-3" name="password" type="password" />
          <label className="label">角色</label>
          <select className="input mb-4" name="role" defaultValue="user">
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          <button className="btn btn-primary w-full">
            <UserPlus className="h-4 w-4" />
            创建
          </button>
          {notice ? <div className="mt-4 text-sm text-slate-500">{notice}</div> : null}
        </form>
      </div>
    </Shell>
  );
}

function AdminUsage() {
  const [events, setEvents] = useState<UsageEvent[]>([]);
  const [status, setStatus] = useState("");
  const load = () => api<{ usageEvents: UsageEvent[] }>(`/api/admin/usage-events${status ? `?status=${status}` : ""}`).then((data) => setEvents(data.usageEvents));
  useEffect(() => {
    void load();
  }, [status]);
  return (
    <Shell area="admin">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-ink">使用记录</h1>
        <select className="input w-40" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">全部</option>
          <option value="success">success</option>
          <option value="error">error</option>
        </select>
      </div>
      <div className="panel overflow-hidden rounded-lg">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="table-th">状态</th>
              <th className="table-th">用户</th>
              <th className="table-th">智能体</th>
              <th className="table-th">模型</th>
              <th className="table-th">Token</th>
              <th className="table-th">耗时</th>
              <th className="table-th">时间</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td className="table-td">
                  {event.status === "success" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" />success</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-700" title={event.error_message}><XCircle className="h-4 w-4" />error</span>
                  )}
                </td>
                <td className="table-td">{event.user_email}</td>
                <td className="table-td">{event.agent_name}</td>
                <td className="table-td">{event.model_name}</td>
                <td className="table-td">{event.tokens_in}/{event.tokens_out}</td>
                <td className="table-td">{event.latency_ms}ms</td>
                <td className="table-td">{new Date(event.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/" element={<EntrancePage />} />
        <Route path="/login" element={<Navigate to="/user/login" replace />} />
        <Route path="/user" element={<Navigate to="/user/login" replace />} />
        <Route path="/user/login" element={<LoginPage mode="user" />} />
        <Route path="/user/workbench" element={<ProtectedRoute loginPath="/user/login"><WorkbenchPage /></ProtectedRoute>} />
        <Route path="/user/agents/:slug" element={<ProtectedRoute loginPath="/user/login"><ChatPage /></ProtectedRoute>} />
        <Route path="/workbench" element={<Navigate to="/user/workbench" replace />} />
        <Route path="/agents/:slug" element={<LegacyAgentRedirect />} />
        <Route path="/admin/login" element={<LoginPage mode="admin" />} />
        <Route path="/admin" element={<ProtectedRoute loginPath="/admin/login"><AdminRoute><AdminDashboard /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/agents" element={<ProtectedRoute loginPath="/admin/login"><AdminRoute><AdminAgents /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/model-configs" element={<ProtectedRoute loginPath="/admin/login"><AdminRoute><AdminModelConfigs /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute loginPath="/admin/login"><AdminRoute><AdminUsers /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/usage" element={<ProtectedRoute loginPath="/admin/login"><AdminRoute><AdminUsage /></AdminRoute></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
